import { observer } from 'mobx-react-lite';
import { FC, MouseEventHandler, ReactElement } from 'react';

import { PayloadType } from 'APP/model/message/messageModel.types';
import type { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

import Advertisement from './Payloads/Advertisement';
import Album from './Payloads/Album';
import Article from './Payloads/Article';
import { AudioPayload } from './Payloads/AudioPayload/AudioPayload';
import { ButtonsPayload } from './Payloads/ButtonsPayload/ButtonsPayload';
import Contact from './Payloads/Contact';
import { DeletedByMePayload } from './Payloads/DeletedByMePayload/DeletedByMePayload';
import { E2ee } from './Payloads/E2ee/E2ee';
import { FilePayload } from './Payloads/FilePayload/FilePayload';
import Image from './Payloads/Image';
import Location from './Payloads/Location';
import { MiniatureMessageSticker } from './Payloads/MiniatureMessageSticker/MiniatureMessageSticker';
import Text from './Payloads/Text';
import { VideoPayload } from './Payloads/VideoPayload/VideoPayload';
import Voice from './Payloads/Voice';

type TMiniatureMessageType =
  | PayloadType.AudioMessage
  | PayloadType.Contact
  | PayloadType.File
  | PayloadType.Image
  | PayloadType.Album
  | PayloadType.Location
  | PayloadType.Sticker
  | PayloadType.RichText
  | PayloadType.ButtonsSelected
  | PayloadType.Video
  | PayloadType.VoiceMessage
  | PayloadType.Article
  | PayloadType.Advertisement
  | PayloadType.Buttons
  | PayloadType.E2ee
  | PayloadType.MessageWasDeletedByMe;

const payloads: Record<
  TMiniatureMessageType,
  FC<{
    titleClassName?: string;
    textClassName?: string;
    iconClassName?: string;
    message: ChatMessage;
    title: string | ReactElement;
    onClick?: MouseEventHandler;
    preventInteraction?: boolean;
  }>
> = {
  [PayloadType.AudioMessage]: AudioPayload,
  [PayloadType.Contact]: Contact,
  [PayloadType.File]: FilePayload,
  [PayloadType.Image]: Image,
  [PayloadType.Album]: Album,
  [PayloadType.Location]: Location,
  [PayloadType.Sticker]: MiniatureMessageSticker,
  [PayloadType.RichText]: Text,
  [PayloadType.E2ee]: E2ee,
  [PayloadType.ButtonsSelected]: Text,
  [PayloadType.Video]: VideoPayload,
  [PayloadType.VoiceMessage]: Voice,
  [PayloadType.Article]: Article,
  [PayloadType.Advertisement]: Advertisement,
  [PayloadType.Buttons]: ButtonsPayload,
  [PayloadType.MessageWasDeletedByMe]: DeletedByMePayload,
};

interface IMiniatureMessageProps {
  titleClassName?: string;
  textClassName?: string;
  iconClassName?: string;
  message: ChatMessage | null;
  title: string | ReactElement;
  onClick?: MouseEventHandler;
  preventInteraction?: boolean;
}

export const MiniatureMessage: FC<IMiniatureMessageProps> = observer((props) => {
  const {
    titleClassName,
    textClassName,
    iconClassName,
    message,
    title,
    onClick,
    preventInteraction,
  } = props;

  if (!message) {
    return null;
  }

  const Component = payloads[message.payload.payloadType as TMiniatureMessageType];

  if (!Component) {
    return null;
  }

  return (
    <Component
      titleClassName={titleClassName}
      textClassName={textClassName}
      iconClassName={iconClassName}
      preventInteraction={preventInteraction}
      message={message}
      title={title}
      onClick={onClick}
    />
  );
});
